import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Upload, X, Video, ImageIcon, Car, MapPin, ShieldCheck, Camera, AlertTriangle, Store, Info, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import LocationPicker from "@/components/LocationPicker";
import VehicleModal from "@/components/VehicleModal";

const STANDARD_CATEGORIES = ["Engine","Transmission","Brakes","Electrical","Suspension","Body","Tires","HVAC","Exhaust","Other"];
const EV_CATEGORIES = ["Battery Diagnostics","Charging System","Regenerative Brakes","12V Battery","Tires","Software / Firmware Update"];

export default function PostIssue() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // Vehicle passed from Dashboard (selected in MY GARAGE)
  const dashboardVehicle = location.state?.vehicle || null;

  // Vehicle step state
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [vehicleStepDone, setVehicleStepDone] = useState(false);
  const [showDifferentVehicle, setShowDifferentVehicle] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    fuel_type: "",
    location: "",
  });
  const [geo, setGeo] = useState({ lat: null, lng: null, radius: 10 });
  const [files, setFiles] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [urgentModal, setUrgentModal] = useState(false);
  const [urgentConfig, setUrgentConfig] = useState({ require_payment: false, fee_usd: 6.99, radius_miles: 25 });
  const [urgentSending, setUrgentSending] = useState(false);
  const [nearbyCount, setNearbyCount] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  // Owner agreement checklist
  const AGREEMENT_ITEMS = [
    "I will not share personal contact details in the issue description.",
    "I agree to mark this issue resolved once it is fixed.",
    "I understand mechanics respond voluntarily and response is not guaranteed.",
    "I have read and agree to ShiftQuote's community guidelines.",
    "I understand ShiftQuote is not responsible for any quote sent by a repair shop.",
    "I understand ShiftQuote is a marketplace platform, not a mechanic or repair service.",
    "I understand that quotes provided by shops are estimates only and are not final until the shop has inspected the vehicle in person.",
  ];
  const [agreedItems, setAgreedItems] = useState(Array(7).fill(false));
  const allAgreed = agreedItems.every(Boolean);
  const toggleAgreement = (i) =>
    setAgreedItems((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  // Load vehicles — if a vehicle was passed from the dashboard, use it immediately
  useEffect(() => {
    if (dashboardVehicle) {
      // Pre-select the vehicle passed from the Owner Dashboard
      setSelectedVehicle(dashboardVehicle);
      setForm((f) => ({
        ...f,
        vehicle_make: dashboardVehicle.make,
        vehicle_model: dashboardVehicle.model,
        vehicle_year: String(dashboardVehicle.year),
        fuel_type: dashboardVehicle.fuel_type,
      }));
      setVehicleStepDone(true);
      setVehiclesLoading(false);
      // Still load all vehicles in background for "use different vehicle"
      api.get("/vehicles").then((res) => setVehicles(res.data)).catch(() => {});
    } else {
      // No vehicle pre-selected — load and auto-select only if single vehicle
      api.get("/vehicles").then((res) => {
        setVehicles(res.data);
        if (res.data.length === 1) {
          const v = res.data[0];
          setSelectedVehicle(v);
          setForm((f) => ({
            ...f,
            vehicle_make: v.make,
            vehicle_model: v.model,
            vehicle_year: String(v.year),
            fuel_type: v.fuel_type,
          }));
          setVehicleStepDone(true);
        }
      }).catch(() => {}).finally(() => setVehiclesLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load urgent feature config
  useEffect(() => {
    api.get("/urgent/config").then((r) => setUrgentConfig(r.data)).catch(() => {});
  }, []);

  // Fetch live nearby-shop count whenever the urgent modal opens.
  useEffect(() => {
    if (!urgentModal) return;
    setNearbyLoading(true);
    setNearbyCount(null);
    const params = new URLSearchParams();
    if (geo.lat != null) params.set("lat", geo.lat);
    if (geo.lng != null) params.set("lng", geo.lng);
    params.set("radius_miles", urgentConfig.radius_miles || 25);
    api.get(`/urgent/nearby-count?${params.toString()}`)
      .then((r) => setNearbyCount(r.data))
      .catch(() => setNearbyCount({ count: 0, has_location: false }))
      .finally(() => setNearbyLoading(false));
  }, [urgentModal, geo.lat, geo.lng, urgentConfig.radius_miles]);

  // Prefill from profile
  useEffect(() => {
    if (user) {
      const loc = user.location || {};
      setGeo((g) => ({
        lat: loc.lat ?? g.lat,
        lng: loc.lng ?? g.lng,
        radius: user.radius_miles ?? g.radius ?? 10,
      }));
      if (loc.city || loc.zip) {
        setForm((f) => ({ ...f, location: f.location || [loc.city, loc.state, loc.zip].filter(Boolean).join(", ") }));
      }
    }
  }, [user]);

  const MAX_BYTES = 400 * 1024 * 1024;
  const MAX_LABEL = "400MB";
  const fmtMB = (b) => `${(b / (1024 * 1024)).toFixed(b > 100 * 1024 * 1024 ? 0 : 1)}MB`;

  const handleFiles = async (filesList) => {
    setUploading(true);
    for (const f of filesList) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is ${fmtMB(f.size)} — max is ${MAX_LABEL}.`);
        continue;
      }
      const id = `${f.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((prev) => [...prev, { id, name: f.name, size: f.size, progress: 0, status: "uploading" }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
          onUploadProgress: (evt) => {
            const total = evt.total || f.size;
            const pct = total ? Math.min(100, Math.round((evt.loaded * 100) / total)) : 0;
            setUploads((prev) => prev.map((u) => u.id === id ? { ...u, progress: pct } : u));
          },
        });
        setFiles((prev) => [...prev, { path: res.data.path, content_type: res.data.content_type, name: f.name, size: f.size }]);
        setUploads((prev) => prev.filter((u) => u.id !== id));
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Upload failed";
        setUploads((prev) => prev.map((u) => u.id === id ? { ...u, status: "failed", error: msg } : u));
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setUploading(false);
  };

  const dismissUpload = (id) => setUploads((prev) => prev.filter((u) => u.id !== id));
  const removeFile = (path) => setFiles((prev) => prev.filter((p) => p.path !== path));

  const selectVehicle = (v) => {
    setSelectedVehicle(v);
    setForm((f) => ({
      ...f,
      vehicle_make: v.make,
      vehicle_model: v.model,
      vehicle_year: String(v.year),
      fuel_type: v.fuel_type,
      // Reset category if switching between EV and non-EV
      category: "",
    }));
    setVehicleStepDone(true);
    setShowDifferentVehicle(false);
  };

  const handleVehicleSave = async (data) => {
    setVehicleSaving(true);
    try {
      const res = await api.post("/vehicles", data);
      toast.success("Vehicle saved");
      setShowVehicleModal(false);
      // Select the new vehicle
      const newVehicle = { ...res.data, fuel_type: data.fuel_type };
      setVehicles((prev) => [newVehicle, ...prev]);
      selectVehicle(newVehicle);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save vehicle");
    } finally {
      setVehicleSaving(false);
    }
  };

  // Determine categories based on fuel_type
  const categories = form.fuel_type === "electric" ? EV_CATEGORIES : STANDARD_CATEGORIES;

  const submit = async () => {
    if (!geo.lat || !geo.lng) {
      toast.error("Please pin your location on the map before posting.");
      return;
    }
    if (!form.title || !form.description) {
      toast.error("Title and description required");
      return;
    }
    if (!form.category) {
      toast.error("Pick a category");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
        file_paths: files.map((f) => f.path),
        lat: geo.lat,
        lng: geo.lng,
        radius_miles: geo.lat != null ? parseInt(geo.radius) || 10 : null,
      };
      const res = await api.post("/issues", payload);
      toast.success("Issue posted — live for nearby shops!");
      navigate(`/issue/${res.data.issue_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 && detail.includes("3 active posts")) {
        toast.error("You have 3 active posts. Please close or mark one as resolved before posting again.");
      } else {
        toast.error(detail || "Failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sendUrgent = async () => {
    if (!form.title || !form.description) {
      toast.error("Title and description required first");
      setUrgentModal(false);
      return;
    }
    if (!form.category) {
      toast.error("Pick a category first");
      setUrgentModal(false);
      return;
    }
    setUrgentSending(true);
    try {
      try {
        const cap = await import("@capacitor/haptics");
        await cap.Haptics.impact({ style: "heavy" });
      } catch { /* web — skip */ }

      let payment_intent_id = null;
      if (urgentConfig.require_payment) {
        const r = await api.post("/urgent/create-payment-intent", {});
        const intent = r.data;
        if (!intent?.payment_intent_id) {
          toast.error("Payment setup failed");
          setUrgentSending(false);
          return;
        }
        payment_intent_id = intent.payment_intent_id;
        toast.error("Stripe payment UI not yet wired. Contact support.");
        setUrgentSending(false);
        return;
      }

      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        vehicle_make: form.vehicle_make || null,
        vehicle_model: form.vehicle_model || null,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
        location: form.location || null,
        file_paths: files.map((f) => f.path),
        lat: geo.lat,
        lng: geo.lng,
        payment_intent_id,
      };
      const res = await api.post("/urgent/dispatch", payload);
      toast.success(`🚨 Alert sent to ${res.data.shops_notified} shops nearby!`, { duration: 6000 });
      setUrgentModal(false);
      navigate(`/issue/${res.data.issue_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "rate_limited") {
        toast.error(detail.message || "Rate limited — try again later");
      } else {
        toast.error(typeof detail === "string" ? detail : (e?.message || "Failed to send urgent alert"));
      }
    } finally {
      setUrgentSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="label-eyebrow mb-3">NEW ISSUE · ANONYMOUS</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-10">Describe the problem.</h1>

        {/* STEP 1: Vehicle Selection */}
        <div className="mb-8 border border-white/10 bg-[#121214] p-6" data-testid="vehicle-step">
          <div className="flex items-center gap-3 mb-4">
            <Car className="w-5 h-5 text-[#FF3B30]" strokeWidth={1.8} />
            <div className="label-eyebrow text-zinc-400">STEP 1 — YOUR VEHICLE</div>
          </div>

          {vehiclesLoading ? (
            <div className="text-sm text-zinc-600">Loading vehicles...</div>
          ) : vehicleStepDone && selectedVehicle && !showDifferentVehicle ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-200">
                <span className="font-semibold text-white">
                  {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
                </span>
                <span className="text-zinc-500 ml-2">·</span>
                <span className="text-zinc-400 ml-2 capitalize">{selectedVehicle.fuel_type}</span>
              </div>
              <button
                onClick={() => setShowDifferentVehicle(true)}
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
                data-testid="use-different-vehicle-btn"
              >
                Use a different vehicle
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {vehicles.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Your saved vehicles</div>
                  {vehicles.map((v) => (
                    <button
                      key={v.vehicle_id}
                      onClick={() => selectVehicle(v)}
                      className={`w-full text-left p-3 border transition-colors flex items-center justify-between ${
                        selectedVehicle?.vehicle_id === v.vehicle_id
                          ? "border-[#FF3B30]/50 bg-[#FF3B30]/5"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      data-testid={`select-vehicle-${v.vehicle_id}`}
                    >
                      <span className="text-sm text-zinc-200">
                        {v.year} {v.make} {v.model}
                        <span className="text-zinc-500 ml-2">·</span>
                        <span className="text-zinc-400 ml-2 capitalize">{v.fuel_type}</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-zinc-600" />
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowVehicleModal(true)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF3B30] hover:text-[#FF3B30]/80 transition-colors border border-[#FF3B30]/30 px-3 py-2.5 hover:bg-[#FF3B30]/5 w-full justify-center"
                data-testid="add-vehicle-inline-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add a New Vehicle
              </button>
              {showDifferentVehicle && selectedVehicle && (
                <button
                  onClick={() => setShowDifferentVehicle(false)}
                  className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
                >
                  Cancel — keep current vehicle
                </button>
              )}
            </div>
          )}
        </div>

        {/* STEP 2: Issue Details (only show after vehicle step) */}
        {vehicleStepDone && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-6">
              <div className="border border-white/10 bg-[#121214] p-6 sm:p-8 space-y-5">
                <div>
                  <label className="label-eyebrow block mb-2">TITLE *</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-base" placeholder="Grinding noise on left turns" data-testid="issue-title-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">DESCRIPTION *</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="input-base" placeholder="When did it start? What does it feel/sound like?" data-testid="issue-description-input" />
                </div>

                <div>
                  <label className="label-eyebrow block mb-2">CATEGORY *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="input-base"
                    data-testid="issue-category-select"
                  >
                    <option value="">Pick the part of the car...</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {form.fuel_type === "electric" && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-[#007AFF]">
                      ⚡ Showing EV-specific service categories
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label-eyebrow block mb-2">MAKE</label>
                    <input value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} className="input-base" placeholder="Toyota" data-testid="issue-make-input" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">MODEL</label>
                    <input value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="input-base" placeholder="Camry" data-testid="issue-model-input" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">YEAR</label>
                    <input type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} className="input-base" placeholder="2018" data-testid="issue-year-input" />
                  </div>
                </div>

                <div>
                  <label className="label-eyebrow block mb-2">LOCATION (ZIP / CITY)</label>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-base" placeholder="94107" data-testid="issue-location-input" />
                  <p className="text-[11px] text-zinc-500 mt-1.5">City or ZIP only. Shops see an approximate area until you accept a quote; the shop you accept gets your exact pin.</p>
                </div>

                {/* Geo radius */}
                <div className="border border-white/10 bg-[#09090B] p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="label-eyebrow flex items-center gap-2"><MapPin className="w-3 h-3 text-[#FF3B30]" /> SHOP VISIBILITY RADIUS</div>
                    <div className="font-heading text-2xl font-black text-[#FF3B30]">{geo.radius}<span className="text-xs text-zinc-500 ml-1">MI</span></div>
                  </div>

                  <LocationPicker
                    lat={geo.lat}
                    lng={geo.lng}
                    onChange={(lat, lng) => setGeo((g) => ({ ...g, lat, lng }))}
                    radiusMiles={geo.radius}
                    showRadius
                    height="280px"
                  />

                  <div>
                    <label className="label-eyebrow block mb-2">RADIUS — {geo.radius} MI</label>
                    <input
                      type="range"
                      min="5"
                      max="100"
                      step="5"
                      value={geo.radius}
                      onChange={(e) => setGeo({ ...geo, radius: parseInt(e.target.value) })}
                      className="w-full accent-[#FF3B30]"
                      data-testid="issue-radius-slider"
                    />
                  </div>

                  {geo.lat == null ? (
                    <div className="text-[11px] text-[#FF9500]">📍 You must pin your location before posting — this helps shops near you find your request.</div>
                  ) : (
                    <div className="text-[11px] text-zinc-500">Only shops inside the red circle will be able to see this job.</div>
                  )}
                </div>
              </div>

              {/* File upload */}
              <div className="border border-dashed border-white/20 bg-[#121214] p-8">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="label-eyebrow">MEDIA · ORIGINAL QUALITY · UP TO 400MB EACH</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => cameraRef.current?.click()}
                      className="btn-secondary flex items-center gap-2"
                      data-testid="take-photo-btn"
                    >
                      <Camera className="w-4 h-4" /> Take Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="btn-secondary flex items-center gap-2"
                      data-testid="upload-file-btn"
                    >
                      <Upload className="w-4 h-4" /> Upload
                    </button>
                  </div>
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*,video/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                    data-testid="camera-input"
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                    data-testid="file-input"
                  />
                </div>
                {/* Active upload progress */}
                {uploads.length > 0 && (
                  <div className="mb-3 space-y-2" data-testid="upload-progress-list">
                    {uploads.map((u) => {
                      const failed = u.status === "failed";
                      return (
                        <div key={u.id} className="border border-white/10 bg-[#09090B] p-3" data-testid={`upload-progress-${u.id}`}>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {failed ? (
                                <X className="w-4 h-4 text-[#FF3B30] shrink-0" />
                              ) : (
                                <Upload className={`w-4 h-4 shrink-0 ${u.progress >= 100 ? "text-[#34C759]" : "text-[#FF3B30] animate-pulse"}`} />
                              )}
                              <div className="min-w-0">
                                <div className="text-xs truncate font-medium">{u.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                                  {fmtMB(u.size)}{!failed && ` · ${u.progress}%`}{u.progress >= 100 && !failed && " · finalizing..."}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {failed && (
                                <span className="text-[10px] uppercase tracking-widest text-[#FF3B30]">Failed</span>
                              )}
                              {failed && (
                                <button onClick={() => dismissUpload(u.id)} className="text-zinc-500 hover:text-white" data-testid={`dismiss-upload-${u.id}`}>
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="h-1 bg-white/5 overflow-hidden">
                            <div
                              className={`h-full transition-all duration-200 ${failed ? "bg-[#FF3B30]" : u.progress >= 100 ? "bg-[#34C759]" : "bg-[#FF3B30]"}`}
                              style={{ width: `${failed ? 100 : u.progress}%` }}
                            />
                          </div>
                          {failed && u.error && (
                            <div className="text-[10px] text-[#FF3B30] mt-1.5 truncate">{u.error}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {files.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-700" strokeWidth={1.2} />
                    <div className="text-sm">Drag photos or videos of the problem area.</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {files.map((f) => (
                      <div key={f.path} className="relative border border-white/10 bg-[#09090B] p-3 flex items-center gap-3">
                        {f.content_type?.startsWith("image/") ? <ImageIcon className="w-5 h-5 text-[#007AFF]" /> : <Video className="w-5 h-5 text-[#FF9500]" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs truncate">{f.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-600">{f.content_type?.split("/")[1]}</div>
                        </div>
                        <button onClick={() => removeFile(f.path)} className="text-zinc-500 hover:text-white" data-testid={`remove-file-${f.path}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <aside className="lg:col-span-5 space-y-6">
              <div className="border border-white/10 bg-[#121214] p-6">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className="w-6 h-6 text-[#34C759]" strokeWidth={1.5} />
                  <div className="text-sm text-zinc-300">
                    <strong className="text-white">Private by design.</strong> Shops see your photos, description, vehicle and an approximate area (about 1 km), plus an anonymous handle. Your name and contact stay private until you accept a quote; the shop you accept then gets your first name, phone and exact location.
                  </div>
                </div>
                <ul className="text-xs text-zinc-500 space-y-2 mb-5 pl-9">
                  <li>• High-resolution photos & 4K videos up to 400MB each</li>
                  <li>• Only nearby shops can see this job</li>
                  <li>• Shops reply with private itemized quotes</li>
                </ul>
              </div>

              <div className="border border-white/10 bg-[#121214] p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Car className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />
                  <div className="text-sm text-zinc-400">
                    Ready when you are. Post once, get private quotes back fast.
                  </div>
                </div>
                {/* 3-post limit reminder */}
                <div className="flex items-start gap-2 bg-zinc-800/80 border border-white/10 rounded px-3 py-2.5 mb-4">
                  <Info className="w-4 h-4 text-zinc-300 mt-0.5 shrink-0" />
                  <p className="text-xs text-white leading-snug">
                    You can only have 3 active posts at a time. Close or mark one as resolved before posting again.
                  </p>
                </div>
                {/* Owner agreement checklist */}
                <div className="bg-zinc-800/80 border border-white/10 rounded px-3 py-3 mb-3">
                  <p className="text-xs font-semibold text-zinc-300 mb-2 uppercase tracking-wide">Before you post, please confirm:</p>
                  <div className="space-y-2">
                    {AGREEMENT_ITEMS.map((item, i) => (
                      <label key={i} className="flex items-start gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={agreedItems[i]}
                          onChange={() => toggleAgreement(i)}
                          className="mt-0.5 accent-blue-500 shrink-0"
                        />
                        <span className={`text-xs leading-snug ${agreedItems[i] ? "text-white" : "text-zinc-400"}`}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={submit} disabled={submitting || geo.lat == null || !allAgreed} className={`btn-primary w-full${(geo.lat == null || !allAgreed) ? " opacity-50 cursor-not-allowed" : ""}`} data-testid="submit-issue-btn">
                  {submitting ? "..." : "Post Anonymously"}
                </button>
              </div>

              {/* URGENT FEATURE HIDDEN - not ready yet */}
            </aside>
          </div>
        )}

        {/* Show prompt if no vehicle step done yet and not loading */}
        {!vehicleStepDone && !vehiclesLoading && vehicles.length === 0 && (
          <div className="text-center py-8 border border-dashed border-white/10 p-12">
            <Car className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
            <div className="text-zinc-400 mb-4">Add your vehicle to continue</div>
            <button
              onClick={() => setShowVehicleModal(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="add-vehicle-to-continue-btn"
            >
              <Plus className="w-4 h-4" /> Add Your Vehicle
            </button>
          </div>
        )}
      </main>

      {/* Vehicle Modal */}
      <VehicleModal
        open={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        onSave={handleVehicleSave}
        vehicle={null}
        saving={vehicleSaving}
      />

      {/* URGENT FEATURE HIDDEN - not ready yet */}
    </div>
  );
}
